import { inject } from 'aurelia';
import { IRouter } from '@aurelia/router';
import { SchedulerModel } from '../../model/scheduler-model';
import { SerieSettingEntity } from '../../apis/serie/serie-setting-entity';
import { TimePart } from '../../infrastructure/time-part';
import { SchedulerModelService } from '../../services/scheduler-model-service';
import { ProjectModelService } from '../../services/project-model-service';
import { EventEntity } from '../../apis/event/event-entity';
import { GroupEntity } from '../../apis/group/group-entity';

@inject(IRouter, SchedulerModelService, ProjectModelService)
export class EditSerie {

    private _schedulerModel: SchedulerModel = null;
    private _router: IRouter = null;
    private _periods: number = null;
    private _fields: number = null;
    private _startTime: string = null;
    private _schedulerModelService: SchedulerModelService;
    private _projectModelService: ProjectModelService;
    private _event: EventEntity = null;
    private _redirecting: boolean = false;
    private _groups: GroupEntity[] = [];
    public selectedGroupIds: string[] = [];
    // Snapshots captured at load time to detect user-made changes
    private _originalPeriods: number = null;
    private _originalFields: number = null;
    private _originalStartTime: string = null;
    private _originalStartDayOffset: number = null;
    private _originalGroupIds: string[] = [];
    private _originalPeriodLength: number = null;
    private _originalPeriodPause: number = null;
    private _originalGamePause: number = null;
    private _originalGameCount: number = null;
    private _originalName: string = null;
    constructor(router: IRouter, schedulerModelService: SchedulerModelService, projectModelService: ProjectModelService) {
        this._router = router;
        this._schedulerModelService = schedulerModelService;
        this._projectModelService = projectModelService;
    }

    public async canLoad(params: any): Promise<boolean> {
        if (params.eventId == null) {
            setTimeout(() => void this._router.load('create'), 0);
            return false;
        }
        if (this._projectModelService.currentModel == null) {
            try {
                await this._projectModelService.loadByEventId(params.eventId, false);
            } catch {
                setTimeout(() => void this._router.load('create'), 0);
                return false;
            }
        }
        return true;
    }

    public loading(params: any): void {
        const model = this._projectModelService.currentModel;
        this._event = model.event;
        this._groups = model.groups;

        if (params.scheduleId != null) {
            this._schedulerModel = this._schedulerModelService.loadByScheduleId(params.scheduleId);
        } else {
            this._schedulerModel = this._schedulerModelService.createNew(params.eventId);
        }

        if (this._schedulerModel.groupIds.length > 0) {
            this.selectedGroupIds = [...this._schedulerModel.groupIds];
        } else if (this._groups.length === 1) {
            this.selectedGroupIds = [this._groups[0].id];
        } else {
            this.selectedGroupIds = [];
        }
        if (this._schedulerModel.startDayOffset > this.dayCount - 1) {
            this._schedulerModel.startDayOffset = 0;
        }
        this._periods = this._schedulerModel.serie.periods;
        this._fields = this._schedulerModel.serie.fields;
        this._startTime = this.serie.startTime.toString();
        this._originalPeriods = this._periods;
        this._originalFields = this._fields;
        this._originalStartTime = this._startTime;
        this._originalStartDayOffset = this._schedulerModel.startDayOffset;
        this._originalGroupIds = [...this.selectedGroupIds];
        this._originalPeriodLength = this.serie.periodLength;
        this._originalPeriodPause = this.serie.periodPause;
        this._originalGamePause = this.serie.gamePause;
        this._originalGameCount = this.serie.gameCount;
        this._originalName = this._schedulerModel.name;
    }

    public get isRedirecting(): boolean {
        return this._redirecting;
    }

    public get groups(): GroupEntity[] {
        return this._groups;
    }

    public get hasGroups(): boolean {
        return this._groups.length > 0;
    }

    public isGroupSelected(groupId: string): boolean {
        return this.selectedGroupIds.includes(groupId);
    }

    public toggleGroupSelection(groupId: string): void {
        if (this.selectedGroupIds.includes(groupId)) {
            this.selectedGroupIds = this.selectedGroupIds.filter(id => id !== groupId);
        } else {
            this.selectedGroupIds = [...this.selectedGroupIds, groupId];
        }
    }


    public get hideDay(): boolean {
        return this.dayCount <= 1;
    }

     public get hidePeriodPause(): boolean {
        return this._periods < 2 ? true : false;
    }

    public get dayCount(): number {
        if (this._event == null || this._event.startDate == null || this._event.endDate == null) {
            return 1;
        }
        const diff = this._event.startDate.diffDays(this._event.endDate);
        return diff >= 0 ? diff + 1 : 1;
    }

    public get days(): number[] {
        const result: number[] = [];
        for (let day = 1; day <= this.dayCount; day++) {
            result.add(day);
        }
        return result;
    }

    public get name(): string {
        return this._schedulerModel.name;
    }

    public set name(value: string) {
        this._schedulerModel.name = value;
    }

    public get serie(): SerieSettingEntity {
        return this._schedulerModel.serie;
    }

    public set serie(value: SerieSettingEntity) {
        this._schedulerModel.serie = value;
    }

    public get startDayOffset(): string {
        return (this._schedulerModel.startDayOffset + 1).toString();
    }

    public set startDayOffset(value: string) {
        let day = parseInt(value, 10);
        if (isNaN(day) || day < 1) {
            day = 1;
        }
        if (day > this.dayCount) {
            day = this.dayCount;
        }
        this._schedulerModel.startDayOffset = day - 1;
    }

    public get periods(): string {
        return this._periods.toString();
    }

    public set periods(value) {
        this._periods = parseInt(value, 10);
    }

    public get startTime(): string {
        return this._startTime;
    }

    public set startTime(value: string) {
        this._startTime = value;
    }
    public get fields(): string {
        return this._fields.toString();
    }

    public set fields(value: string) {
        this._fields = parseInt(value, 10);
    }

    public get gameCount(): string {
        return this.serie.gameCount.toString();
    }

    public set gameCount(value: string) {
        this.serie.gameCount = parseInt(value, 10);
    }

    public get periodLength(): number {
        return this.serie.periodLength;
    }

    public set periodLength(value) {
        this.serie.periodLength = value;
    }

    public get periodPause(): number {
        return this.serie.periodPause;
    }

    public set periodPause(value) {
        this.serie.periodPause = value;
    }

    public get gamePause(): number {
        return this.serie.gamePause;
    }

    public set gamePause(value) {
        this.serie.gamePause = value;
    }

    public get minTeamCount(): number {
        return this._fields > 2 ? Math.round(this._fields / 0.44) : 0;
    }

    public cancel(): void {
        void this._router.load(`create/event-summary/${this._schedulerModel.eventId}`);
    }

    public get canFinish(): boolean {
        return this._schedulerModel.scheduleRowCollections.any();
    }

    public finish(): void {
        if (this.recalculationNeeded) {
            this._schedulerModel.hasBreakingChanges = true;
        }
        this.serie.periods = this._periods;
        this.serie.fields = this._fields;
        this.serie.startTime = TimePart.fromString(this._startTime);
        this._schedulerModel.groupIds = [...this.selectedGroupIds];
        if (this.selectedGroupIds.length > 0) {
            this._schedulerModel.teamSettings = this._projectModelService.getTeamsForGroups(this.selectedGroupIds);
        }
        const serieSettingsChanged =
            this._periods !== this._originalPeriods ||
            this._fields !== this._originalFields ||
            this._startTime !== this._originalStartTime ||
            this._schedulerModel.startDayOffset !== this._originalStartDayOffset ||
            this.serie.periodLength !== this._originalPeriodLength ||
            this.serie.periodPause !== this._originalPeriodPause ||
            this.serie.gamePause !== this._originalGamePause ||
            this.serie.gameCount !== this._originalGameCount ||
            this._schedulerModel.name !== this._originalName ||
            this.selectedGroupIds.length !== this._originalGroupIds.length ||
            this.selectedGroupIds.some(id => !this._originalGroupIds.includes(id));
        this._projectModelService.finalizeSchedule(this._schedulerModel, serieSettingsChanged);
        void this._router.load(`create/event-summary/${this._schedulerModel.eventId}`);
    }

    public get recalculationNeeded(): boolean {
        return this._schedulerModel.scheduleRowCollections.any() && (this._fields !== this.serie.fields || this._originalGameCount !== this.serie.gameCount);
    }

    public next(): void {
        if (this.recalculationNeeded) {
            this._schedulerModel.hasBreakingChanges = true;
        }
        this.serie.periods = this._periods;
        this.serie.fields = this._fields;
        this.serie.startTime = TimePart.fromString(this._startTime);
        this._schedulerModel.groupIds = [...this.selectedGroupIds];

        // Get teams for all selected groups from the project cache (covers groups
        // not originally linked to this schedule that wouldn't be in the model yet)
        if (this.selectedGroupIds.length > 0) {
            this._schedulerModel.teamSettings = this._projectModelService.getTeamsForGroups(this.selectedGroupIds);
        }

        void this._router.load('create/edit-schedule');
    }
}
